import { AnalyticsEvent } from '../../common/Analytics';
import * as telegramStars from '../../utils/telegramStars';
import type { ConvertCurrencyFn } from '../ServerReplicantExchangeRate';

export const TELEGRAM_STARS_CURRENCY_CODE = 'XTR';

export type PurchaseProvider = 'apple' | 'facebook' | 'google' | 'komoju' | 'stripe' | 'telegram';

export type PurchaseProviderMethod =
    | 'apple_native'
    | 'facebook_app'
    | 'google_native'
    | 'komoju_au'
    | 'komoju_aupay'
    | 'komoju_credit_card'
    | 'komoju_docomo'
    | 'komoju_linepay'
    | 'komoju_paidy'
    | 'komoju_paypay'
    | 'komoju_rakutenpay'
    | 'komoju_softbank'
    | 'stripe_applePay'
    | 'stripe_googlePay'
    | 'telegram_stars';

const METHOD_FEE_PERCENTAGES: Partial<Record<PurchaseProviderMethod, number>> = {
    komoju_au: 15,
    komoju_aupay: 15,
    komoju_credit_card: 3.6, // Use the most common card brand fee, i.e., Visa's 3.6%
    komoju_docomo: 15,
    komoju_linepay: 3.5,
    komoju_paidy: 9,
    komoju_paypay: 9,
    komoju_rakutenpay: 8.4,
    komoju_softbank: 15,
};

/**
 * @param amount Amount in the smallest currency unit such as 100 US cents.
 * @returns Equivalent amount in base currency unit such as $1, or `undefined` in case of an unexpected currency.
 */
export function getAmountInBaseCurrencyUnit(amount: number, currency: string): number | void {
    switch (currency) {
        case 'JPY':
            return amount;
        case 'EUR':
        case 'USD':
            return amount / 100;
        default:
            break;
    }
}

/** @returns Revenue event properties or `void` in case of unsupported purchase currency or provider method. */
export async function getRevenueProperties(
    purchase: {
        currency: string;
        price: number;
        provider: PurchaseProvider;
        providerMethod: PurchaseProviderMethod;
    },
    convert: ConvertCurrencyFn,
): Promise<{
    currencyCodeLocal: string;
    currencyCodeSource: string;
    dollarToLocalRate?: number;
    processorFee: number;
    processorFeeLocal?: number;
    processorFeePercent: number;
    purchaseProvider: PurchaseProvider;
    purchaseProviderMethod: PurchaseProviderMethod;
    revenueGross: number;
    revenueGrossLocal?: number;
    revenueNet: number;
    revenueNetLocal?: number;
    sourceToLocalRate?: number;
} | void> {
    let revenueGross: number | undefined;

    if (purchase.currency === TELEGRAM_STARS_CURRENCY_CODE) {
        revenueGross = telegramStars.convertToUSD(purchase.price);
    } else {
        const sourceToUSDConversion = await convert({
            fromCurrency: purchase.currency,
            toCurrency: 'USD',
            value: purchase.price,
        });

        if (sourceToUSDConversion) {
            revenueGross = sourceToUSDConversion.value;
        }
    }

    if (revenueGross == undefined) {
        return; // Unknown source revenue currency
    }

    let processorFee: number | undefined;

    if (purchase.currency === TELEGRAM_STARS_CURRENCY_CODE) {
        processorFee = telegramStars.getProcessorFeeUSD(purchase.price);
    } else if (purchase.provider === 'apple' || purchase.provider === 'google' || purchase.provider === 'facebook') {
        processorFee = 0.3 * revenueGross;
    } else if (purchase.provider === 'stripe') {
        // TODO this calculation is based on US Stripe account with foreign card fee, so will need to be adjusted if we change Stripe country registration or receive payments with US cards.
        // Total fee for purchases made with non-US cards through our current US Stripe account:
        processorFee = 0.044 * revenueGross + 0.3;
    } else {
        const feePercentage = METHOD_FEE_PERCENTAGES[purchase.providerMethod];

        if (feePercentage !== undefined) {
            processorFee = feePercentage * 0.01 * revenueGross;
        }
    }

    if (processorFee === undefined) {
        return; // Unknown purchase provider method
    }

    const processorFeePercent = (processorFee / revenueGross) * 100;

    const revenueNet = revenueGross - processorFee;

    const currencyCodeSource = purchase.currency;
    const currencyCodeLocal = process.env.REVENUE_CURRENCY || currencyCodeSource;

    let revenueGrossLocal: number | undefined;
    let revenueNetLocal: number | undefined;
    let sourceToLocalRate: number | undefined;
    let processorFeeLocal: number | undefined;
    let dollarToLocalRate: number | undefined;

    const revenueGrossToLocalConversion = await convert({
        fromCurrency: 'USD',
        toCurrency: currencyCodeLocal,
        value: revenueGross,
    });

    if (revenueGrossToLocalConversion) {
        revenueGrossLocal = revenueGrossToLocalConversion.value;
        sourceToLocalRate = purchase.price / revenueGrossLocal;

        processorFeeLocal = revenueGrossLocal * (processorFeePercent / 100);
        revenueNetLocal = revenueGrossLocal - processorFeeLocal;
        dollarToLocalRate = revenueGrossToLocalConversion.exchangeRate;
    }

    return {
        currencyCodeLocal,
        currencyCodeSource,
        dollarToLocalRate,
        revenueGross,
        processorFee,
        processorFeePercent,
        processorFeeLocal,
        purchaseProvider: purchase.provider,
        purchaseProviderMethod: purchase.providerMethod,
        revenueGrossLocal,
        revenueNet,
        revenueNetLocal,
        sourceToLocalRate,
    };
}

export async function createRevenueValidateEvent(opts: {
    convertCurrency: ConvertCurrencyFn;
    currency: string;
    extraEventProperties?: Record<string, unknown>;
    price: number;
    productId: string;
    provider: PurchaseProvider;
    providerMethod: PurchaseProviderMethod;
    userId: string;
}): Promise<AnalyticsEvent<'RevenueValidate'> | void> {
    const revenueEventProps = await getRevenueProperties(opts, opts.convertCurrency);

    if (revenueEventProps) {
        return {
            user_id: opts.userId,
            event_type: 'RevenueValidate',
            event_properties: {
                ...opts.extraEventProperties,
                ...revenueEventProps,
                productID: opts.productId,
                revenueType: 'in_app_purchase',
            },
        };
    }
}
